// Fase 8 — Panoramica / Work Queue cross-tenant della receptionist.
// View derivata da /api/agency/work-queue (nessuna nuova collection).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneCall, AlertTriangle, Clock, MessageSquare, UserCog, Store,
  CalendarCheck, CheckCircle2, ClipboardList,
} from "lucide-react";
import { api, createAgencyClient } from "@/lib/api";
import { callbackTone, fmtTime, isOverdue, isDueNow } from "@/lib/callbacks";
import CallbackDetail from "@/pages/admin/agency/CallbackDetail";
import CallbackModal from "@/components/CallbackModal";
import AgencyContactDrawerHost from "@/pages/admin/agency/AgencyContactDrawerHost";
import { CallButton } from "@/context/VoiceContext";

function Kpi({ label, value, tone }) {
  return (
    <div className="sp-card px-4 py-3 flex-1 min-w-[130px]" data-testid={`wq-kpi-${label}`}>
      <div className={`text-2xl font-black ${tone || ""}`}>{value === null || value === undefined ? "—" : value}</div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</div>
    </div>
  );
}

function TenantTag({ tenant }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-brand)]">
      <Store className="h-3 w-3" /> {tenant?.name || "—"}
    </span>
  );
}

function Section({ title, icon: Icon, accent, count, children, tid }) {
  if (!count) return null;
  return (
    <div className="sp-card p-4" data-testid={tid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${accent || ""}`} />
        <h2 className={`font-display text-sm font-black uppercase tracking-wider ${accent || ""}`}>{title}</h2>
        <span className="text-xs font-bold text-[color:var(--sp-text-muted)]">{count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function fmtDelay(min) {
  if (min < 60) return `${min} min`;
  const d = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  const m = min % 60;
  if (d > 0) return `${d}g ${h}h`;
  return `${h}h ${m}m`;
}

function CbRow({ cb, onClick, showDelay }) {
  const tone = callbackTone(cb);
  const delayMin = showDelay ? Math.round((Date.now() - new Date(cb.scheduled_at).getTime()) / 60000) : 0;
  return (
    <div data-testid={`wq-cb-${cb.id}`}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]">
      <button onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <div className="text-center w-12 flex-shrink-0"><div className="font-black text-sm">{fmtTime(cb.scheduled_at)}</div></div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-sm truncate">{cb.client?.name || "Contatto"}</div>
          <TenantTag tenant={cb.tenant} />
        </div>
        {showDelay && delayMin > 0
          ? <span className="text-[10px] font-bold text-rose-600 flex-shrink-0">⚠ {fmtDelay(delayMin)} di ritardo</span>
          : <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold flex-shrink-0 ${tone.cls}`}>{tone.label}</span>}
      </button>
      <CallButton testid={`wq-call-${cb.id}`} compact
        target={{ client_id: cb.client_id, tenant_id: cb.tenant?.id, callback_id: cb.id, agency_case_id: cb.agency_case_id, clientName: cb.client?.name, tenantName: cb.tenant?.name }} />
    </div>
  );
}

const REPORT_PERIODS = [
  { key: "today", label: "Oggi" }, { key: "week", label: "Settimana" },
  { key: "month", label: "Mese" }, { key: "custom", label: "Personalizzato" },
];

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function StatKpi({ label, value, tone, testid }) {
  return (
    <div className="rounded-xl border border-[color:var(--sp-border)] px-3 py-2 text-center" data-testid={testid}>
      <div className={`text-xl font-black ${tone || ""}`}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</div>
    </div>
  );
}

function VoiceStats() {
  const [period, setPeriod] = useState("today");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [recs, setRecs] = useState(null);   // null = non-admin (nessun selettore)
  const [recId, setRecId] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [clientIds, setClientIds] = useState([]);
  const [st, setSt] = useState(null);

  useEffect(() => {
    api.get("/admin/receptionists").then((r) => setRecs(r.data || [])).catch(() => setRecs(null));
    api.get("/agency/tenants").then((r) => setTenants(r.data || [])).catch(() => setTenants([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams({ period });
      if (period === "custom") { if (!from || !to) { setSt(null); return; } p.set("date_from", from); p.set("date_to", to); }
      if (recId) p.set("receptionist_id", recId);
      if (tenantId) p.set("tenant_id", tenantId);
      if (clientIds.length) p.set("client_ids", clientIds.join(","));
      const r = await api.get(`/agency/voice/stats?${p}`);
      setSt(r.data);
    } catch { setSt(null); }
  }, [period, from, to, recId, tenantId, clientIds]);
  useEffect(() => { load(); }, [load]);

  const contacts = st?.contacts || [];

  return (
    <div className="sp-card p-4 mb-5" data-testid="wq-voice-stats">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h2 className="font-display text-sm font-black uppercase tracking-wider flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[color:var(--sp-brand)]" /> Statistiche chiamate
        </h2>
        {tenants.length > 0 && (
          <select data-testid="stats-salon" value={tenantId} onChange={(e) => { setTenantId(e.target.value); setClientIds([]); }}
            className="sp-input !w-auto !py-1 text-xs">
            <option value="">Tutti i saloni</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {recs && (
          <select data-testid="stats-receptionist" value={recId} onChange={(e) => { setRecId(e.target.value); setClientIds([]); }}
            className="sp-input !w-auto !py-1 text-xs">
            <option value="">Tutte le receptionist</option>
            {recs.map((r) => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
          </select>
        )}
        {contacts.length > 0 && (
          <details className="relative" data-testid="stats-contact-filter">
            <summary className="list-none cursor-pointer sp-input !w-auto !py-1 text-xs inline-flex items-center gap-1 select-none">
              {clientIds.length ? `${clientIds.length} contatti` : "Tutti i contatti"}
            </summary>
            <div className="absolute z-30 mt-1 max-h-56 w-60 overflow-auto sp-card p-2 shadow-lg">
              {clientIds.length > 0 && (
                <button data-testid="stats-contact-clear" onClick={() => setClientIds([])}
                  className="text-[10px] font-bold text-[color:var(--sp-brand)] mb-1">Azzera selezione</button>
              )}
              {contacts.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-xs py-1 cursor-pointer" data-testid={`stats-contact-opt-${c.id}`}>
                  <input type="checkbox" checked={clientIds.includes(c.id)}
                    onChange={() => setClientIds((prev) => prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id])} />
                  <span className="truncate">{c.name}</span>
                </label>
              ))}
            </div>
          </details>
        )}
        <div className="flex gap-1 ml-auto flex-wrap">
          {REPORT_PERIODS.map((p) => (
            <button key={p.key} data-testid={`report-period-${p.key}`} onClick={() => { setPeriod(p.key); setClientIds([]); }}
              className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${period === p.key ? "bg-[color:var(--sp-brand)] text-white border-transparent" : "border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]"}`}>{p.label}</button>
          ))}
        </div>
      </div>
      {period === "custom" && (
        <div className="flex gap-2 mb-3 flex-wrap items-center">
          <input type="date" data-testid="report-date-from" className="sp-input !w-auto !py-1.5 text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-xs text-[color:var(--sp-text-muted)]">→</span>
          <input type="date" data-testid="report-date-to" className="sp-input !w-auto !py-1.5 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      )}
      {!st ? (
        <p className="text-xs text-[color:var(--sp-text-muted)] italic">{period === "custom" && (!from || !to) ? "Seleziona un intervallo di date." : "Nessun dato per il periodo."}</p>
      ) : (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <StatKpi testid="stats-calls" label="Chiamate" value={st.total_calls} />
            <StatKpi testid="stats-answered" label="Risposte" value={st.answered} tone="text-emerald-600" />
            <StatKpi testid="stats-noanswer" label="Non risposte" value={st.not_answered} tone="text-rose-600" />
            <StatKpi testid="stats-contacts" label="Contatti" value={st.distinct_contacts} />
            <StatKpi testid="stats-booked" label="Appuntamenti" value={st.booked_count} tone="text-sky-600" />
            <StatKpi testid="stats-conversion" label="Conversion" value={`${st.conversion_rate}%`} tone="text-[color:var(--sp-brand)]" />
          </div>
          {st.booked?.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] font-black uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-2">Appuntamenti fissati</div>
              <div className="space-y-1.5" data-testid="stats-booked-list">
                {st.booked.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm rounded-lg border border-[color:var(--sp-border)] px-3 py-1.5">
                    <CalendarCheck className="h-3.5 w-3.5 text-sky-600 flex-shrink-0" />
                    <span className="font-bold truncate">{b.client_name}</span>
                    <span className="text-xs text-[color:var(--sp-text-muted)] truncate">{b.services?.join(", ")}</span>
                    <span className="ml-auto text-xs whitespace-nowrap">{fmtDT(b.start)}</span>
                    {b.tenant_name && <span className="text-[10px] font-bold text-[color:var(--sp-brand)] whitespace-nowrap hidden sm:inline">{b.tenant_name}</span>}
                    {!recId && recs && b.receptionist_name && <span className="text-[10px] text-[color:var(--sp-text-muted)] whitespace-nowrap hidden md:inline">· {b.receptionist_name}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-3 italic">Risposte = chiamate connesse ≥20s. Appuntamenti conteggiati solo se realmente creati in agenda. Conversion = appuntamenti ÷ contatti chiamati.</p>
        </>
      )}
    </div>
  );
}

export default function AgencyOverviewPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [contact, setContact] = useState(null);
  const [followUp, setFollowUp] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/agency/work-queue"); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const groups = useMemo(() => {
    const cbs = data?.callbacks || [];
    const now = new Date();
    const overdue = [], dueNow = [], upcoming = [];
    cbs.forEach((cb) => {
      if (isOverdue(cb, now)) overdue.push(cb);
      else if (isDueNow(cb, now)) dueNow.push(cb);
      else upcoming.push(cb);
    });
    return { overdue, dueNow, upcoming };
  }, [data]);

  const openContactFromCase = (c) => setContact({ client_id: c.client_id, tenant: c.tenant, case: c, agency_case_id: c.id });
  const openLead = (l) => navigate(`/admin/agency/lead-inbox?client=${encodeURIComponent(l.thread_key)}`);

  const kpi = data?.kpi || {};
  const nothing = data && groups.overdue.length === 0 && groups.dueNow.length === 0 &&
    (data.lead_unread || []).length === 0 && (data.human_action || []).length === 0 &&
    groups.upcoming.length === 0 && (data.to_review || []).length === 0;

  return (
    <div data-testid="aw-overview-page">
      <h1 className="font-display text-2xl font-black mb-4">Panoramica</h1>

      {/* KPI giornalieri leggeri */}
      <div className="flex flex-wrap gap-3 mb-5">
        <Kpi label="Chiamate oggi" value={kpi.calls_today} />
        <Kpi label="Richiami scaduti" value={kpi.overdue} tone={kpi.overdue ? "text-rose-600" : ""} />
        <Kpi label="Nuove risposte" value={kpi.new_replies} tone={kpi.new_replies ? "text-[color:var(--sp-brand)]" : ""} />
        <Kpi label="Appuntamenti oggi" value={kpi.appts_today} />
      </div>

      <VoiceStats />

      {loading ? (
        <div className="text-center text-sm text-[color:var(--sp-text-muted)] py-10">Carico…</div>
      ) : nothing ? (
        <div className="sp-card p-10 text-center" data-testid="wq-empty">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-3" />
          <h2 className="font-display text-xl font-black">Tutto sotto controllo</h2>
          <p className="text-sm text-[color:var(--sp-text-muted)] mt-1">Non ci sono attività urgenti.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Richiami scaduti" icon={AlertTriangle} accent="text-rose-600" count={groups.overdue.length} tid="wq-overdue">
            {groups.overdue.map((cb) => <CbRow key={cb.id} cb={cb} showDelay onClick={() => setDetail(cb)} />)}
          </Section>
          <Section title="Da chiamare ora" icon={PhoneCall} accent="text-amber-700" count={groups.dueNow.length} tid="wq-due-now">
            {groups.dueNow.map((cb) => <CbRow key={cb.id} cb={cb} onClick={() => setDetail(cb)} />)}
          </Section>
          <Section title="Nuove risposte lead" icon={MessageSquare} accent="text-[color:var(--sp-brand)]" count={(data.lead_unread || []).length} tid="wq-lead-unread">
            {(data.lead_unread || []).map((l) => (
              <button key={l.thread_key} data-testid={`wq-lead-${l.thread_key}`} onClick={() => openLead(l)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] text-left">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">{l.client?.name || "Contatto"}</div>
                  <TenantTag tenant={l.tenant} />
                  <div className="text-xs text-[color:var(--sp-text-muted)] truncate">{l.last_message}</div>
                </div>
                <span className="h-2 w-2 rounded-full bg-[color:var(--sp-brand)] flex-shrink-0" />
              </button>
            ))}
          </Section>
          <Section title="Intervento umano" icon={UserCog} accent="text-purple-600" count={(data.human_action || []).length} tid="wq-human">
            {(data.human_action || []).map((c) => (
              <button key={c.id} data-testid={`wq-human-${c.id}`} onClick={() => openContactFromCase(c)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] text-left">
                <div className="min-w-0 flex-1"><div className="font-bold text-sm truncate">{c.client?.name || "Contatto"}</div><TenantTag tenant={c.tenant} /></div>
              </button>
            ))}
          </Section>
          <Section title="Prossime chiamate" icon={Clock} count={groups.upcoming.length} tid="wq-upcoming">
            {groups.upcoming.map((cb) => <CbRow key={cb.id} cb={cb} onClick={() => setDetail(cb)} />)}
          </Section>
          <Section title="Da controllare" icon={CalendarCheck} count={(data.to_review || []).length} tid="wq-to-review">
            {(data.to_review || []).map((c) => (
              <button key={c.id} data-testid={`wq-review-${c.id}`} onClick={() => openContactFromCase(c)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] text-left">
                <div className="min-w-0 flex-1"><div className="font-bold text-sm truncate">{c.client?.name || "Contatto"}</div><TenantTag tenant={c.tenant} /></div>
              </button>
            ))}
          </Section>
        </div>
      )}

      {detail && (
        <CallbackDetail callback={detail} tenantClient={createAgencyClient(() => detail.tenant?.id)} onClose={() => setDetail(null)} onChanged={load}
          onOpenContact={(cb) => { setDetail(null); setContact(cb); }}
          onScheduleFollowUp={(cb) => setFollowUp(cb)} />
      )}
      {contact && (
        <AgencyContactDrawerHost clientId={contact.client_id} tenant={contact.tenant}
          caseObj={contact.case ? { id: contact.agency_case_id, ...contact.case } : null}
          onClose={() => setContact(null)} onUpdated={load} />
      )}
      {followUp && (
        <CallbackModal tenant={followUp.tenant} clientId={followUp.client_id} clientName={followUp.client?.name}
          caseObj={followUp.agency_case_id ? { id: followUp.agency_case_id } : null}
          onClose={() => setFollowUp(null)} onSaved={load} />
      )}
    </div>
  );
}
